import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useCallback } from "react";
import { Flex, Form, Typography, Upload, message } from "antd";
import { DeleteOutlined, LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import { useAuthStore } from "../../../../../stores/stores";
import { BASE_URL_V2 } from "../../../../../config/api/configApiv2";
import CustomCard from "../../../../custom-card/CustomCard";
import { FormInstance } from "antd/lib";
import "./index.scss";
import FormItemInput from "../../../../form-input/FormInput";

interface InfoAuthorProps {
  form?: FormInstance;
  dataConfig?: any;
}

const InfoAuthor = ({ form, dataConfig }: InfoAuthorProps) => {
  const { t } = useTranslation();
  const [loadingUpload, setLoadingUpload] = useState<Record<number, boolean>>({});
  const [uploadedImages, setUploadedImages] = useState<Record<number, string>>({});
  const accessToken = useAuthStore((state) => state.accessToken);
  const [assignerData, setAssignerData] = useState(dataConfig?.assigner ?? []);

  const beforeUploadImage = useCallback((file: File) => {
    const isValid = ["image/jpeg", "image/png"].includes(file.type);
    const isLt2M = file.size / 1024 / 1024 <= 4;

    if (!isValid) message.error(t("Ảnh yêu cầu định dạng jpeg/png!", "Ảnh yêu cầu định dạng jpeg/png!"));
    if (!isLt2M) message.error(t("Ảnh phải có dung lượng nhỏ hơn 4MB!", "Ảnh phải có dung lượng nhỏ hơn 4MB!"));

    return isValid && isLt2M;
  }, []);

  const handleChange = useCallback((info: any, id: number) => {
    if (info.file.status === "uploading") {
      setLoadingUpload((prev) => ({ ...prev, [id]: true }));
      return;
    }

    if (info.file.status === "done") {
      setLoadingUpload((prev) => ({ ...prev, [id]: false }));

      const filePath = info.file?.response?.filePath;
      const fileName = info.file?.response?.fileName;

      if (filePath && fileName) {
        // Hiển thị ảnh bằng filePath
        setUploadedImages((prev) => ({ ...prev, [id]: filePath }));

        // Cập nhật ảnh hiển thị trong assignerData
        setAssignerData((prev: any) =>
          prev.map((assigner: any) => ({
            ...assigner,
            assigned: assigner.assigned.map((teacher: any) =>
              teacher.assignUserId === id
                ? { ...teacher, imageCourseAssign: filePath }
                : teacher
            )
          }))
        );

        // Cập nhật ảnh gửi về form (fileName)
        form?.setFieldsValue({
          assignerAvatar: [
            ...(form.getFieldValue("assignerAvatar") || []).filter(
              (item: any) => item.id !== id
            ),
            { id, image: fileName }
          ]
        });

        message.success(t("Tải ảnh lên thành công!", "Tải ảnh lên thành công!"));
      }
    }

    if (info.file.status === "error") {
      setLoadingUpload((prev) => ({ ...prev, [id]: false }));
      message.error(t("Tải ảnh lên thất bại!", "Tải ảnh lên thất bại!"));
    }
  }, [form]);

  const handleRemoveImage = (id: number) => {
    setUploadedImages((prev) => ({ ...prev, [id]: "" }));
    setAssignerData((prev: any) =>
      prev.map((assigner: any) => ({
        ...assigner,
        assigned: assigner.assigned.map((teacher: any) =>
          teacher.assignUserId === id
            ? { ...teacher, imageCourseAssign: null }
            : teacher
        )
      }))
    );

    form?.setFieldsValue({
      assignerAvatar: (form.getFieldValue("assignerAvatar") || []).filter(
        (item: any) => item.id !== id
      )
    });
  };

  useEffect(() => {
    if (dataConfig?.assigner) {
      setAssignerData(dataConfig.assigner);
    }
  }, [dataConfig]);

  const renderUpload = (teacher: any) => {
    const rawPath = uploadedImages[teacher.assignUserId] || teacher.imageCourseAssign;
    const id = teacher.assignUserId;

    return (
      <Form.Item
        name="assignerAvatar"
        label={t("Ảnh tác giả", "Ảnh tác giả")}
        className="author-upload--avatar"
      >
        <div className="image-container">
          <Upload
            listType="picture-card"
            className="avatar-uploader"
            showUploadList={false}
            action={`${BASE_URL_V2}/mooc-course-block-quiz/upload-file`}
            headers={{ Authorization: `Bearer ${accessToken}` }}
            beforeUpload={beforeUploadImage}
            onChange={(e) => handleChange(e, id)}
          >
            {loadingUpload[id] ? (
              <LoadingOutlined />
            ) : rawPath ? (
              <img src={rawPath} alt="avatar" style={{ width: "100%" }} />
            ) : (
              <div className="upload-content">
                <PlusOutlined /> <span>{t("Tải lên", "Tải lên")}</span>
              </div>
            )}
          </Upload>
          {rawPath && (
            <span className="remove-icon">
              <a
                className="upload-action"
                onClick={(e) => {
                  e.preventDefault();
                  handleRemoveImage(id);
                }}
              >
                <DeleteOutlined />
              </a>
            </span>
          )}
        </div>
        <Typography.Text className="sub-title">
          {t("Độ phân giải đề xuất của ảnh khi tải lên là 640*640, kích thước file tối đa 4MB.", "Độ phân giải đề xuất của ảnh khi tải lên là 640*640, kích thước file tối đa 4MB.")}
        </Typography.Text>
      </Form.Item>
    );
  };

  return (
    <CustomCard title={t("Tác giả", "Tác giả")} className="wrapper-settings--card">
      <div className="form-setting--school">
        {assignerData.map((assigner: any, idx: number) => {
          const hasConstruct = assigner.assigned?.some((t: any) => t.construct);
          if (!hasConstruct) return null;

          return (
            <section key={idx}>
              <div className={assigner.isSponsor ? "form--main_school" : "form--secondary_school"}>
                <div className={assigner.isSponsor ? "form--main_school__title" : "form--secondary_school__title"}>
                  <Typography.Title level={5}>
                    {assigner.isSponsor ? t("Trường chủ trì", "Trường chủ trì") : t("Trường phối hợp", "Trường phối hợp")}: {assigner.enterpriseName}
                  </Typography.Title>
                </div>

                {assigner.assigned.map((teacher: any, tIdx: number) => (
                  <section key={tIdx} className="lecturers">
                    <Flex gap="middle" vertical>
                      <Form.Item label={assigner.isSponsor ? t("Giảng viên chủ trì", "Giảng viên chủ trì") : t("Giảng viên hỗ trợ", "Giảng viên hỗ trợ")}>
                        <FormItemInput value={teacher.assignUserName} readOnly />
                      </Form.Item>
                      {renderUpload(teacher)}
                    </Flex>
                  </section>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </CustomCard>
  );
};

export default InfoAuthor;
